import { Interval } from 'luxon';
import React from 'react';
import styled from 'styled-components';

import { useLocalStorage } from 'common/hooks';
import { useLanguage } from 'locale';
import { useVenueAdsFreeTrialConfig } from 'modules/campaigns';

import Disclaimer from '../components/Disclaimer';
import ErrorPage from '../components/ErrorPage';
import IdentityVerificationReminder from '../components/IdentityVerification/IdentityVerificationReminder';
import { InsightSummary } from '../components/InsightSummary';
import LoadingPage from '../components/LoadingPage';
import { OverallPerformance } from '../components/OverallPerformance';
import WoltPlusBanner from '../components/WoltPlusBanner/WoltPlusBanner';
import {
  getWoltPlusBannerLink,
  shouldDisplayWoltPlusBanner,
  reportBannerClick,
} from '../components/WoltPlusBanner/utils';
import { useImageCoverageTotalInsights } from '../components/image-coverage/hooks/useImageCoverageTotalInsights';
import useNewAlivenessFeatureFlag from '../hooks/useAlivenessFeatureFlag';
import useDeliveryTypeChecks from '../hooks/useDeliveryTypeChecks';
import {
  useCustomersTotalInsightV2WithPreviousInterval,
  useOfflineTimeOverviewInsightWithPreviousInterval,
  usePerformanceInsightV2WithPreviousInterval,
  usePreparationInsightWithPreviousInterval,
  useRatingsOverviewInsightWithPreviousInterval,
  useRefundsOverviewInsightWithPreviousInterval,
  useRejectionsInsightWithPreviousInterval,
  useTotalRejectedOrdersInsightWithPreviousInterval,
} from '../hooks/useInsightApi';
import { useInsightSubRoutes } from '../hooks/useInsightsSubRoutes';
import { useIsRefundOverviewEnabled } from '../hooks/useIsRefundOverviewEnabled';
import useMenuInsights, { DEFAULT_MENU_SORT_STATE } from '../hooks/useMenuInsights';
import { useTelemetryBusinessInsightV2View } from '../telemetry';
import { InsightEntity } from '../types';
import { isAllError, isInitialLoading } from '../utils';

type IndexPageProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  isVATEnabled: boolean;
};

const Footer = styled.div`
  padding-inline: 24px;
  padding-block: 48px 64px;
`;

const getInsightEntityCountry = (insightEntity: InsightEntity) => {
  if (insightEntity.type === 'venue') {
    return insightEntity.venue.country;
  }

  if (insightEntity.type === 'merchant') {
    return insightEntity.merchant.country;
  }

  return;
};

// This is the next version of merchant insights for merchant and venue owners.
// The project's epic ticket: https://woltwide.atlassian.net/browse/MAXX-409
const IndexPage = ({ insightEntity, dateInterval, isVATEnabled }: IndexPageProps) => {
  const { performanceLink } = useInsightSubRoutes();
  const language = useLanguage();
  useTelemetryBusinessInsightV2View(insightEntity.type);
  const insightEntityCountry = getInsightEntityCountry(insightEntity);
  const [shouldDisplayBanner, setShouldDisplayBanner] = useLocalStorage(
    'wolt-plus-banner',
    shouldDisplayWoltPlusBanner({
      country: insightEntityCountry,
      isWoltPlus: insightEntity.isWoltPlus,
    }).toString(),
  );

  const { isNewAlivenessEnabled, isLoading: isAlivenessFeatureFlagLoading } = useNewAlivenessFeatureFlag(
    insightEntity.type,
    insightEntity.id,
  );

  const { isRefundOverviewEnabled, isLoading: isRefundOverviewFeatureFlagLoading } = useIsRefundOverviewEnabled(
    insightEntity.type,
    insightEntity.id,
  );

  const { isSelfDeliveryOnlyVenue, isHybridDeliveryVenue } = useDeliveryTypeChecks(insightEntity);

  const performanceInsightV2 = usePerformanceInsightV2WithPreviousInterval({
    insightEntity,
    dateInterval,
  });
  const customersTotalInsight = useCustomersTotalInsightV2WithPreviousInterval({
    insightEntity,
    dateInterval,
  });

  const ratingsInsight = useRatingsOverviewInsightWithPreviousInterval({ insightEntity, dateInterval });
  const preparationInsight = usePreparationInsightWithPreviousInterval({ insightEntity, dateInterval });
  const totalRejectedOrdersInsight = useTotalRejectedOrdersInsightWithPreviousInterval({ insightEntity, dateInterval });
  const menuItemsInsight = useMenuInsights({
    ...DEFAULT_MENU_SORT_STATE,
    entityType: insightEntity.type,
    entityId: insightEntity.id,
    interval: dateInterval,
    limit: 5,
  });
  const rejectionInsight = useRejectionsInsightWithPreviousInterval({ insightEntity, dateInterval });
  const offlineTimeInsight = useOfflineTimeOverviewInsightWithPreviousInterval({ insightEntity, dateInterval });
  const refundsOverviewInsight = useRefundsOverviewInsightWithPreviousInterval({
    insightEntity,
    dateInterval: isRefundOverviewEnabled ? dateInterval : null,
  });
  const imageCoverageInsight = useImageCoverageTotalInsights({ insightEntity });

  const venue = insightEntity.type === 'venue' ? insightEntity.venue : null;

  const { config: adsFreeTrialConfig } = useVenueAdsFreeTrialConfig({
    venueId: venue?.id,
    country: venue?.country,
    merchantId: venue?.merchant,
    city: venue?.city,
    productLine: venue?.product_line,
    enabled: !!venue,
  });

  // menuItemsInsight is excluded from here to avoid blocking the page load
  const allInsightResponses = [
    performanceInsightV2,
    ratingsInsight,
    preparationInsight,
    totalRejectedOrdersInsight,
    rejectionInsight,
    offlineTimeInsight,
    refundsOverviewInsight,
    imageCoverageInsight,
  ];

  if (isInitialLoading(allInsightResponses) || isAlivenessFeatureFlagLoading || isRefundOverviewFeatureFlagLoading) {
    return <LoadingPage />;
  }
  if (isAllError(allInsightResponses)) {
    return (
      <ErrorPage
        onRetry={() => {
          allInsightResponses.forEach(insight => insight.refetch());
        }}
      />
    );
  }

  return (
    <>
      {shouldDisplayBanner === 'true' && insightEntityCountry && (
        <WoltPlusBanner
          ctaLink={getWoltPlusBannerLink(insightEntityCountry, language.key)}
          onCtaClick={() => reportBannerClick('TELL_ME_MORE')}
          onDismissClick={() => {
            setShouldDisplayBanner('false');
            reportBannerClick('DISMISS');
          }}
        />
      )}
      <IdentityVerificationReminder />
      <OverallPerformance
        performanceInsight={performanceInsightV2}
        isWoltPlusEnabled={!!insightEntity.isWoltPlus}
        link={performanceLink}
        isVATEnabled={isVATEnabled}
        adsFreeTrialConfig={adsFreeTrialConfig}
      />
      <InsightSummary
        insightEntity={insightEntity}
        customersTotalInsight={customersTotalInsight}
        ratingsInsight={ratingsInsight}
        preparationInsight={preparationInsight}
        totalRejectedOrdersInsight={totalRejectedOrdersInsight}
        menuItemsInsight={menuItemsInsight}
        rejectionInsight={rejectionInsight}
        offlineTimeInsight={offlineTimeInsight}
        refundsOverviewInsight={refundsOverviewInsight}
        isNewAlivenessEnabled={isNewAlivenessEnabled}
        isRefundOverviewEnabled={isRefundOverviewEnabled}
        isVATEnabled={isVATEnabled}
        hidePrepTime={isSelfDeliveryOnlyVenue}
        showHybridDeliveryDisclaimer={isHybridDeliveryVenue}
        totalEntityImageCoverage={imageCoverageInsight.totalEntityImageCoverage}
      />
      <Footer>
        <Disclaimer />
      </Footer>
    </>
  );
};
export default IndexPage;
